/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ResourceType, STPageType, STSlideSizeType } from '@tencent/slide-sdk-pb-model';
import flatten from 'lodash/flatten';
import set from 'lodash/set';
import { assert } from 'ts-essentials';
import themeTemplate from '../template/theme-template.json';
import {
    getSlideKeyframe,
    getLayoutKeyframe,
    getMasterKeyframe,
    getNoteSlideKeyframe,
    getNotesMasterKeyframe,
} from '../keyframe/get-page-keyframe';
import { cloneDeep } from '../utils/clone-deep';
import { DataType } from '../data';
import { createMutations } from '../mutation/mutation-factory';
import { AddSectionMutation } from '../mutation/section/add-section-mutation';
import { RemoveThemeMutation } from '../mutation/theme/remove-theme-mutation';
import { AddThemeMutation } from '../mutation/theme/add-theme-mutation';
import { Rule } from '../mutation/base/mutation-rules';
import { BindSectionToSlideMutation } from '../mutation/section/bind-section-to-slide-mutation';
import { RemoveSectionMutation } from '../mutation/section/remove-section-mutation';
import {
    createIdsMapFromShapes,
    createIdsMapFromTiming,
    getIdsMapFromShapes,
    getIdsMapFromTiming,
} from '../utils/id-map-generator';
import { ThemeContext } from '../context/theme-context';
import { record } from '../utils/recorder';
import { MovePageMutation } from '../mutation/page/move-page-mutation';
import { SetSlideSizeMutation } from '../mutation/presentation/set-slide-size-mutation';
import { UpdateResourceMapMutation } from '../mutation/resource/update-resource-map-mutation';
import { SetPresentationPropertiesMutation } from '../mutation/presentation/set-presentation-properties-mutation';
import { move } from '../utils/move';
import { merge } from '../utils/protoutil';
import {
    SetTextBodyPropertiesMutation,
    TextBodyPropertiesUtils,
} from '../mutation/text/set-text-body-properties-mutation';
import { DeduplicateBuilder } from '../utils/deduplicate/deduplicate-builder';
import { DeduplicateDirector } from '../utils/deduplicate/deduplicate-director';
import { SetTextDataMutation, TextDataUtils } from '../mutation/text/set-text-data-mutation';
import { shallowOmit } from '../utils/object-utils';
import { type ISlideDataModel } from '../model/slide-data-model';
import { SetNotesSizeMutation } from '../mutation/presentation/set-notes-size-mutation';
import { SectionMutationHelper } from './section-mutation-utils/section-mutation-helper';
import { MutationAPI } from './mutation-api';
import type { SmartArtDataModel } from '../model/smart-art-data-model';
import type { INotesMasterDataModel } from '../model/notes-master-data-model';
import type { INoteSlideDataModel } from '../model/note-slide-data-model';
import type { DeepReadonly } from 'ts-essentials';
import type {
    IAudioResourceData,
    IFontResourceData,
    IImageResourceData,
    IResourceMapData,
    IVideoResourceData,
} from '../data/resource-map-data';
import type { IDrawingShapeDataModel, IGroupShapeDataModel } from '../model/group-shape-data-model';
import type {
    ILayoutData,
    IMasterData,
    IMasterDataDelta,
    ISlideData,
    IThemeData,
    INoteSlideData,
    IPresentationDataDelta,
    INotesMasterData,
    IPageData,
    IPresentationData,
    ITableData,
} from '../data';
import type { ILayoutDataModel } from '../model/layout-data-model';
import type { ITableDataModel } from '../model/table-data-model';
import type { IMasterDataModel, MasterDataModel } from '../model/master-data-model';
import type { PresentationDataModel } from '../model/presentation-data-model';
import type { ISectionDataModel, SectionDataModel } from '../model/section-data-model';
import type {
    ICTBaseStylesDelta,
    ICTExtendedGuide,
    ICTTextBody,
    ICTTextBodyProperties,
    ICTTextListStyle,
    ICTTextListStyleDelta,
    ICTTextParagraph,
    ICTTextParagraphProperties,
    ICTSlideMasterTextStylesDelta,
    IWidthInfo,
    IHeightInfo,
} from '@tencent/slide-sdk-pb-model';

export class PresentationMutationAPI extends MutationAPI<PresentationDataModel> {
    /**
     * 在已有的presentation中添加另一个presentation数据
     * @param presentation 要被添加的presentation实例
     * @param index 导入的presentation坐标，默认队尾
     */
    @record()
    public addPresentation(newPresentationData: IPresentationData, toIndex: number, hasSection?: boolean) {
        let masterIdsArr: Map<string, string>[] = [];
        let slideIdsArr: Map<string, string>[] = [];
        let themeIdsMap: Map<string, string> = new Map<string, string>();
        const layoutIdsArr: Map<string, string>[] = [];
        let redundantMasterMap;
        const clonePresentationData = this.shallowClonePresentation(newPresentationData);
        this.transaction(() => {
            this.updateResourceMap(clonePresentationData.resourceMap);
            const deduplicateBuilder = new DeduplicateBuilder();
            const deduplicateDirector = new DeduplicateDirector(deduplicateBuilder);
            deduplicateDirector.directDeduplicate(clonePresentationData, this.model.store.data, {
                layoutRepeatedRate: 0,
            });

            // 添加不重复的主题
            themeIdsMap = this.addTheme(clonePresentationData.themeList);

            // 添加不重复的母版
            clonePresentationData.masterList.forEach((master) => {
                const newId = themeIdsMap.get(master.themeId);
                if (newId) {
                    master.themeId = newId;
                }
            });
            masterIdsArr = this.addMasters(clonePresentationData.masterList);
            redundantMasterMap = deduplicateBuilder.getRedundantMasterMap();

            // 添加不重复的版式
            const newMasterLayoutMap = new Map<string, ILayoutData[]>();
            Object.values(clonePresentationData.layoutMap).forEach((layout) => {
                const newId = masterIdsArr.find((ids) => ids.get(layout.masterId))?.get(layout.masterId);
                if (newId) {
                    layout.masterId = newId;
                }
                if (newMasterLayoutMap.get(layout.masterId)) {
                    newMasterLayoutMap.get(layout.masterId)?.push(layout);
                } else {
                    newMasterLayoutMap.set(layout.masterId, [layout]);
                }
            });
            for (const layoutList of newMasterLayoutMap.values()) {
                layoutIdsArr.push(...this.addLayouts(layoutList));
            }

            // 添加页
            clonePresentationData.slideList.forEach((slide) => {
                const newId = layoutIdsArr.find((ids) => ids.get(slide.layoutId))?.get(slide.layoutId);
                if (newId) {
                    slide.layoutId = newId;
                }
            });
            const addSlides = hasSection ? this.addSlidesWithSection.bind(this) : this.addSlides.bind(this);
            slideIdsArr = addSlides(clonePresentationData.slideList, toIndex);
            Object.values(clonePresentationData.noteSlideMap).forEach((noteSlide) =>
                this.addNoteSlide({
                    ...noteSlide,
                    slideId: slideIdsArr.find((ids) => ids.get(noteSlide.slideId))?.get(noteSlide.slideId) ?? '',
                }),
            );
        });
        return {
            redundantMasterMap,
            masterIdsArr,
            layoutIdsArr,
            themeIdsMap,
            slideIdsArr,
        };
    }

    @record()
    public updateProps(presentationDelta: IPresentationDataDelta) {
        const { showSpecialPlsOnTitleSld, firstSlideNum, defaultTextStyle } = presentationDelta;
        this.transaction(() => {
            this.request([
                new SetPresentationPropertiesMutation({
                    showSpecialPlsOnTitleSld,
                    firstSlideNum,
                    defaultTextStyle:
                        defaultTextStyle && merge(cloneDeep(this.model.store.data.defaultTextStyle), defaultTextStyle),
                }),
            ]);
        });
    }

    /**
     * 目前只支持 4:3 16:9 互相 resize
     * @param targetWidth
     * @param targetHeight
     * @param slideSizeType
     */
    @record()
    public resizeSlideSize(targetWidth: number, targetHeight: number, slideSizeType: STSlideSizeType) {
        if (
            slideSizeType !== STSlideSizeType.screen4x3 &&
            slideSizeType !== STSlideSizeType.screen16x9 &&
            targetWidth / targetHeight !== 4 / 3 &&
            targetWidth / targetHeight !== 16 / 9
        )
            return;
        const { cx: oldSlideWidth, cy: oldSlideHeight } = this.model.props.sldSz;
        let scaleRadio = 0; // 缩放采用的统一比例，防止元素产生形变
        const scaleX = targetWidth / oldSlideWidth;
        const scaleY = targetHeight / oldSlideHeight;
        if (scaleX <= scaleY) {
            scaleRadio = scaleX;
        } else {
            scaleRadio = scaleY;
        }

        this.scalePresentation(targetWidth, targetHeight, slideSizeType, scaleX, scaleY, scaleRadio);
        return true;
    }

    /**
     * 缩放幻灯片到固定比例
     * @param slideSizeType 幻灯片的比例类型，目前支持 4:3 和 16:9
     */
    @record()
    public updateSlideSizeType(sz: Required<STSlideSizeType>) {
        const slideSizeType = this.getValidSldSz(sz);
        const { cx: width, cy: height, type } = this.model.props.sldSz;
        if (type === slideSizeType) {
            return;
        }

        // 后续扩充类型时，需要修改算法实现
        const targetHeight = height;
        const targetWidth = slideSizeType === STSlideSizeType.screen4x3 ? (4 / 3) * height : (16 / 9) * height;

        const scaleX = targetWidth / width;
        const scaleY = 1;
        const offsetX = (targetWidth - width) / 2;
        const offsetY = 0;

        this.transaction(() => {
            this.request([
                new SetSlideSizeMutation({
                    size: {
                        cx: targetWidth,
                        cy: targetHeight,
                        type: slideSizeType,
                    },
                }),
            ]);

            for (const master of this.model.masterList) {
                for (const shape of master.shapeList) {
                    this.scaleShapeFrame(shape, scaleX, scaleY);
                }
            }

            for (const layout of this.model.layoutList) {
                for (const shape of layout.shapeList) {
                    this.scaleShapeFrame(shape, scaleX, scaleY);
                }
            }

            for (const slide of this.model.slideList) {
                for (const shape of slide.shapeList) {
                    if (shape.isPlaceholder && shape.type === DataType.Shape) {
                        this.scaleShapeFrame(shape, scaleX, scaleY);
                    } else {
                        this.moveShapeFrame(shape, offsetX, offsetY);
                    }
                }
            }
        });
    }

    @record()
    public addSlides(slides: ISlideData[], position = this.model.slideList.length) {
        if (!slides.length) return [];

        this.guard((presentation) => Rule.page.can.insertIntoSlideList(presentation, slides[0].id, position));

        const idsMapArr: Array<Map<string, string>> = [];
        this.forEachInTransaction(slides, (slide, index) => {
            const newSlideData: ISlideData = cloneDeep(slide);
            Reflect.deleteProperty(newSlideData, 'noteSlideId');
            const idGenerator = this.model.getIDGenerator();
            const idsMap = this.createIdsMapFromPage(newSlideData);

            // 如果有 timing，需要处理对应的 ids
            if (newSlideData.timing) {
                const timingIdsMap = createIdsMapFromTiming(newSlideData.timing, idGenerator);
                [...timingIdsMap.entries()].forEach(([key, value]) => idsMap.set(key, value));
            }
            const context = this.createKeyframeContext(idsMap);
            const mutations = getSlideKeyframe(newSlideData, position + index, context);
            this.request(createMutations(mutations));
            idsMapArr.push(idsMap);
        });
        return idsMapArr;
    }

    @record()
    public addSlidesWithSection(
        slides: ISlideData[],
        gapIndex: number = this.model.slideList.length + this.model.sectionList.length,
    ) {
        if (!slides.length) return [];

        let idsMapArr: Array<Map<string, string>> = [];
        this.transaction(() => {
            // 在添加 slide 之前，判断节是否需要进行修改
            const { sectionList } = this.model;
            const sectionIndex = this.getSectionIndexFromGap(gapIndex);
            const position = gapIndex - sectionIndex; // 插入的 slide 位置
            const positionSlide = this.model.slideList[position];
            // 只有插入位置是头页的时候，才需要对节进行修改
            const sectionNeedChangeBind = sectionList.filter(
                (section, index) => positionSlide?.id === section.props.firstSlideId && index <= sectionIndex,
            );

            // 添加 slide
            idsMapArr = this.addSlides(slides, position);

            // 修改节的绑定信息
            for (const section of sectionNeedChangeBind) {
                this.bindSectionToSlide(section, position);
            }
        });
        return idsMapArr;
    }

    @record()
    public addLayouts(layouts: ILayoutData[], position?: number) {
        if (!layouts.length) return [];

        this.guard((presentation) =>
            Rule.page.can.insertIntoLayoutIdList(presentation, layouts[0].id, layouts[0].masterId, position ?? 0),
        );

        assert(
            layouts.every((layout) => layout.masterId === layouts[0].masterId),
            'all layouts must have the same master id',
        );

        const idsMapArr: Array<Map<string, string>> = [];
        this.forEachInTransaction(layouts, (layout, index) => {
            const newLayoutData: ILayoutData = cloneDeep(layout);
            position ??= this.model.getMasterById(newLayoutData.masterId)?.layoutList.length ?? 0;
            const idGenerator = this.model.getIDGenerator();
            const idsMap = this.createIdsMapFromPage(newLayoutData);
            if (layout.timing) {
                const timingIdsMap = createIdsMapFromTiming(layout.timing, idGenerator);
                [...timingIdsMap.entries()].forEach(([key, value]) => idsMap.set(key, value));
            }

            const context = this.createKeyframeContext(idsMap);
            const mutations = getLayoutKeyframe(newLayoutData, position + index, context);
            this.request(createMutations(mutations));
            idsMapArr.push(idsMap);
        });
        return idsMapArr;
    }

    @record()
    public addMasters(masters: IMasterData[], position = this.model.masterList.length) {
        if (!masters.length) return [];

        this.guard((presentation) => Rule.page.can.insertIntoMasterList(presentation, masters[0].id, position));

        const idsMapArr: Array<Map<string, string>> = [];
        this.forEachInTransaction(masters, (master, index) => {
            const newMasterData: IMasterData = cloneDeep(master);
            newMasterData.layoutIdList = [];
            const idGenerator = this.model.getIDGenerator();
            const idsMap = this.createIdsMapFromPage(newMasterData);
            if (master.timing) {
                const timingIdsMap = createIdsMapFromTiming(master.timing, idGenerator);
                [...timingIdsMap.entries()].forEach(([key, value]) => idsMap.set(key, value));
            }

            const context = this.createKeyframeContext(idsMap);
            const mutations = getMasterKeyframe(newMasterData, position + index, context);
            this.request(createMutations(mutations));
            idsMapArr.push(idsMap);
        });
        return idsMapArr;
    }

    @record()
    public addNotesMasterSlide(notesMaster: INotesMasterData) {
        const newNotesMasterData: INotesMasterData = cloneDeep(notesMaster);
        const { spTree } = newNotesMasterData.cSld;
        const idGenerator = this.model.getIDGenerator();
        const idsMap: Map<string, string> = spTree ? createIdsMapFromShapes(spTree.shapeList, idGenerator) : new Map();
        idsMap.set(notesMaster.id, idGenerator.next());
        if (spTree) {
            spTree.id = spTree.id ?? `spTree_${notesMaster.id}`;
            idsMap.set(spTree.id, idGenerator.next());
        }

        const context = this.createKeyframeContext(idsMap);
        const mutations = getNotesMasterKeyframe(newNotesMasterData, context);
        this.transaction(() => this.request(createMutations(mutations)));
    }

    @record()
    public addNoteSlide(noteSlide: INoteSlideData): string {
        this.guard((presentation) => Rule.page.should.exist(presentation, noteSlide.slideId, STPageType.slide));
        const slide = this.model.store.getSlideById(noteSlide.slideId);
        assert(slide);

        const currentNoteSlide = slide.getNoteSlide();
        if (currentNoteSlide) {
            return currentNoteSlide.id;
        }

        const newNoteSlideData: INoteSlideData = cloneDeep(noteSlide);
        const { spTree } = newNoteSlideData.cSld;
        const idGenerator = this.model.getIDGenerator();
        const idsMap: Map<string, string> = spTree ? createIdsMapFromShapes(spTree.shapeList, idGenerator) : new Map();
        idsMap.set(noteSlide.id, idGenerator.next());
        if (spTree) {
            spTree.id = spTree.id ?? `spTree_${noteSlide.id}`;
            idsMap.set(spTree.id, idGenerator.next());
        }

        const context = this.createKeyframeContext(idsMap);

        const mutations = getNoteSlideKeyframe(newNoteSlideData, { ...context, page: slide.store.data });
        this.transaction(() => this.request(createMutations(mutations)));
        return noteSlide.id;
    }

    @record()
    public removeSlides(slideDataModels: ISlideDataModel[]) {
        if (!slideDataModels.length) return;

        this.transaction(() => {
            slideDataModels
                .slice()
                .sort((s1, s2) => this.model.slideList.indexOf(s2) - this.model.slideList.indexOf(s1))
                .forEach((slideModel) => {
                    const slideData = slideModel.getWritableOwnedData();
                    const index = this.model.slideList.indexOf(slideModel);
                    if (!slideData.cSld.spTree) return;
                    const idsMap = getIdsMapFromShapes(slideData.cSld.spTree.shapeList);
                    if (slideData.timing) {
                        const timingIdsMap = getIdsMapFromTiming(slideData.timing);
                        [...timingIdsMap.entries()].forEach(([key, value]) => idsMap.set(key, value));
                    }
                    const context = this.createKeyframeContext(idsMap);
                    // 先删除 shapes
                    slideModel.api.deleteShapes(slideModel.shapeList);
                    // 再删除页
                    slideData.cSld.spTree.shapeList = [];
                    const mutationsData = getSlideKeyframe(slideData, index, context);
                    this.request(
                        createMutations(
                            flatten(
                                createMutations(mutationsData)
                                    .map((mutation) => mutation.invert(this.model))
                                    .reverse(),
                            ),
                        ),
                    );
                });
        });
    }

    @record()
    public removeSlidesWithSection(slideDataModels: ISlideDataModel[]) {
        if (!slideDataModels.length) return;

        this.transaction(() => {
            // 节相关操作
            const { slideList, sectionList } = this.model;
            const sectionHelper = new SectionMutationHelper(slideList, sectionList, this.bindSectionToSlide.bind(this));
            sectionHelper.setIgnoreSlides(slideDataModels);
            for (const slide of slideDataModels) {
                sectionHelper.rebindBySlide(slide.id);
            }
            sectionHelper.requestBindTask();
            sectionHelper.updateSectionList(this.model.sectionList); // 经历了节的重新绑定，需要保证 section 的顺序
            sectionHelper.requestRenameTask(sectionList.map((i) => i.props.name));

            // 页相关操作
            this.removeSlides(slideDataModels);
        });
    }

    @record()
    public removeLayouts(layoutDataModels: ILayoutDataModel[]) {
        if (!layoutDataModels.length) return;

        this.guard((presentation) =>
            Rule.every(() =>
                Rule.forEach(layoutDataModels, (layout) =>
                    Rule.page.should.haveNoStaleReference(presentation, layout.id),
                ),
            ),
        );

        this.transaction(() => {
            layoutDataModels
                .slice()
                .sort((l1, l2) => this.model.layoutList.indexOf(l2) - this.model.layoutList.indexOf(l1))
                .forEach((layoutModel) => {
                    this.transaction(() => {
                        const layoutData = layoutModel.getWritableOwnedData();
                        const master = layoutModel.getMaster();
                        const index = master?.layoutList.indexOf(layoutModel) ?? -1;
                        if (!layoutData.cSld.spTree) return;
                        const idsMap = getIdsMapFromShapes(layoutData.cSld.spTree.shapeList);
                        if (layoutData.timing) {
                            const timingIdsMap = getIdsMapFromTiming(layoutData.timing);
                            [...timingIdsMap.entries()].forEach(([key, value]) => idsMap.set(key, value));
                        }
                        const context = this.createKeyframeContext(idsMap);
                        // 先删除 shapes
                        layoutModel.api.deleteShapes(layoutModel.shapeList);
                        // 再删除页
                        layoutData.cSld.spTree.shapeList = [];
                        const mutationsData = getLayoutKeyframe(layoutData, index, context);
                        this.request(
                            createMutations(
                                flatten(
                                    createMutations(mutationsData)
                                        .map((mutation) => mutation.invert(this.model))
                                        .reverse(),
                                ),
                            ),
                        );
                    });
                });
        });
    }

    @record()
    public removeMasters(masterDataModels: IMasterDataModel[]) {
        if (!masterDataModels.length) return;

        this.guard((presentation) =>
            Rule.every(() =>
                Rule.forEach(masterDataModels, (master) =>
                    Rule.page.should.haveNoStaleReference(presentation, master.id),
                ),
            ),
        );

        this.transaction(() => {
            this.model.store.masterList.intersectSorted(masterDataModels).forEach((masterModel) => {
                const masterData = masterModel.getWritableOwnedData();
                const index = this.model.masterList.indexOf(masterModel) ?? -1;
                if (!masterData.cSld.spTree) return;
                const idsMap = getIdsMapFromShapes(masterData.cSld.spTree.shapeList);
                if (masterData.timing) {
                    const timingIdsMap = getIdsMapFromTiming(masterData.timing);
                    [...timingIdsMap.entries()].forEach(([key, value]) => idsMap.set(key, value));
                }
                const context = this.createKeyframeContext(idsMap);
                // 先删除 shapes
                masterModel.api.deleteShapes(masterModel.shapeList);
                // 再删除页
                masterData.cSld.spTree.shapeList = [];
                const mutationsData = getMasterKeyframe(masterData, index, context);
                this.transaction(() => {
                    this.request(
                        createMutations(
                            flatten(
                                createMutations(mutationsData)
                                    .map((mutation) => mutation.invert(this.model))
                                    .reverse(),
                            ),
                        ),
                    );
                });
            });
        });
    }

    @record()
    public removeNoteSlides(noteSlideDataModels: INoteSlideDataModel[]) {
        if (!noteSlideDataModels.length) return;
        this.transaction(() => {
            [...this.model.noteSlideMap.values()].forEach((nodeSlideModel) => {
                const slideData = this.model.slideList
                    .find((slide) => slide.id === nodeSlideData.slideId)
                    ?.getWritableOwnedData() || { id: '', cSld: {}, type: DataType.Slide };
                const nodeSlideData = nodeSlideModel.getWritableOwnedData();
                if (!nodeSlideData.cSld.spTree) return;
                const idsMap = getIdsMapFromShapes(nodeSlideData.cSld.spTree.shapeList);
                const context = this.createKeyframeContext(idsMap);
                // 先删除 shapes
                nodeSlideModel.api.deleteShapes(nodeSlideModel.shapeList);
                // 再删除页
                nodeSlideData.cSld.spTree.shapeList = [];
                const mutationsData = getNoteSlideKeyframe(nodeSlideData, { ...context, page: slideData });
                this.transaction(() => {
                    this.request(
                        createMutations(
                            flatten(
                                createMutations(mutationsData)
                                    .map((mutation) => mutation.invert(this.model))
                                    .reverse(),
                            ),
                        ),
                    );
                });
            });
        });
    }

    @record()
    public removeNodeSlideMasters(nodeSlideMasterDataModels: INotesMasterDataModel[]) {
        if (!nodeSlideMasterDataModels.length) return;

        this.guard((presentation) =>
            Rule.forEach(nodeSlideMasterDataModels, (nodeSlideMasterDataModel) =>
                Rule.every(() => Rule.page.should.haveNoStaleReference(presentation, nodeSlideMasterDataModel.id)),
            ),
        );

        this.transaction(() => {
            [...this.model.notesMasterMap.values()].forEach((noteMasterDataModel) => {
                const nodeMasterData = noteMasterDataModel.getWritableOwnedData();
                if (!nodeMasterData.cSld.spTree) return;
                const idsMap = getIdsMapFromShapes(nodeMasterData.cSld.spTree.shapeList);
                const context = this.createKeyframeContext(idsMap);
                // 先删除 shapes
                noteMasterDataModel.api.deleteShapes(noteMasterDataModel.shapeList);
                // 再删除页
                nodeMasterData.cSld.spTree.shapeList = [];
                const mutationsData = getNotesMasterKeyframe(nodeMasterData, context);
                this.transaction(() => {
                    this.request(
                        createMutations(
                            flatten(
                                createMutations(mutationsData)
                                    .map((mutation) => mutation.invert(this.model))
                                    .reverse(),
                            ),
                        ),
                    );
                });
            });
        });
    }

    @record()
    public moveSlides(slideDataModels: ISlideDataModel[], toIndex: number) {
        const slides = this.model.store.slideList.intersectSorted(slideDataModels);
        if (!slides.length) return;

        this.transaction(() => {
            let offset = 0;
            slides.forEach((slide) => {
                this.transaction(() => {
                    const parentId = slide.getLayout()?.id ?? '';
                    const fromIndex = this.model.slideList.indexOf(slide);
                    const srcIndex = fromIndex >= toIndex ? fromIndex : fromIndex - offset;
                    let destIndex;
                    if (fromIndex >= toIndex) {
                        destIndex = toIndex + offset;
                        offset += 1;
                    } else {
                        destIndex = toIndex - 1;
                    }
                    if (srcIndex === destIndex) return;
                    this.request([
                        new MovePageMutation({
                            pageId: slide.id,
                            pageType: STPageType.slide,
                            srcParentPageId: parentId,
                            srcIndex,
                            destParentPageId: parentId,
                            destIndex,
                        }),
                    ]);
                });
            });
        });
    }

    @record()
    public moveSlidesWithSection(slideDataModels: ISlideDataModel[], toGapIndex: number) {
        const slides = this.model.store.slideList.intersectSorted(slideDataModels);
        if (!slides.length) return;

        this.transaction(() => {
            if (slides.length <= 0) {
                return;
            }
            const { slideList, sectionList } = this.model;
            const sectionHelper = new SectionMutationHelper(slideList, sectionList, this.bindSectionToSlide.bind(this));

            // 找到待移出的 slide 来自哪些节，修改 section 绑定
            sectionHelper.setIgnoreSlides(slides);
            for (const slide of slides) {
                sectionHelper.rebindBySlide(slide.id);
            }
            // 修改移入后 section 绑定
            sectionHelper.rebindWhenInsertSlide(slides[0].id, toGapIndex);

            // 移动 slide
            const toIndex = toGapIndex - this.getSectionIndexFromGap(toGapIndex);
            this.moveSlides(slides, toIndex);

            // 执行节的 mutation
            sectionHelper.requestBindTask();
            sectionHelper.updateSectionList(this.model.sectionList); // 经历了节的重新绑定，需要保证 section 的顺序
            sectionHelper.requestRenameTask(sectionList.map((i) => i.props.name));
        });
    }

    @record()
    public duplicatePages(pageDataModels: Array<ISlideDataModel | ILayoutDataModel>) {
        if (!pageDataModels.length) return;

        if (pageDataModels.some((page) => ![DataType.Slide, DataType.SlideLayout].includes(page.type))) return;
        const pageTypeSet = new Set(pageDataModels.map((page) => page.type));
        if (pageTypeSet.size !== 1) return;

        const pageType = pageDataModels[0].type;
        if (pageType === DataType.Slide) {
            this.duplicateSlides(pageDataModels as ISlideDataModel[]);
        } else if (pageType === DataType.SlideLayout) {
            this.duplicateLayouts(pageDataModels as ILayoutDataModel[]);
        }
    }

    @record()
    public moveLayouts(layoutDataModels: ILayoutDataModel[], toIndex: number, destMasterDataModel: IMasterDataModel) {
        const layoutsByMaster = this.getLayoutsMasterMap(layoutDataModels);
        if (layoutsByMaster.size === 0) return;

        this.transaction(() => {
            for (const [srcMaster, layoutList] of layoutsByMaster.entries()) {
                if (!layoutList.length) continue;

                let offset = 0;
                const layouts = srcMaster.store.layoutList.intersectSorted(layoutList);
                layouts.forEach((layout) => {
                    this.transaction(() => {
                        const srcParentPageId = srcMaster.id;
                        const destParentPageId = destMasterDataModel.id;
                        const isSameMaster = srcMaster === destMasterDataModel;
                        const fromIndex = srcMaster.store.layoutList.findIndexById(layout.id);
                        const srcIndex = fromIndex >= toIndex ? fromIndex : fromIndex - offset;
                        let destIndex;
                        if (fromIndex >= toIndex) {
                            destIndex = toIndex + offset;
                            offset += 1;
                        } else {
                            // 如果是同一个master，那么des位置的索引需要减1，因为移动过程中，src位置的索引会减1
                            destIndex = isSameMaster ? toIndex - 1 : toIndex;
                        }
                        if (srcIndex === destIndex && isSameMaster) return;
                        this.request([
                            new MovePageMutation({
                                pageId: layout.id,
                                pageType: STPageType.slideLayout,
                                srcParentPageId,
                                srcIndex,
                                destParentPageId,
                                destIndex,
                            }),
                        ]);
                    });
                });
            }
        });
    }

    /**
     * 添加一个新的节
     * @param name 新添加节的名称
     * @param toGapIndex 列表间隙的索引，默认为最后
     * @returns 添加后的节索引
     */
    @record()
    public addSection(sectionName: string, toGapIndex?: number) {
        this.guard((presentation) => Rule.section.can.insertIntoGapPosition(presentation, sectionName, toGapIndex));
        const sectionId = this.model.getIDGenerator().next();
        const { sectionList, slideList } = this.model;
        const gapIndex = this.getGapIndex(toGapIndex);

        // 遍历 slideList，当找到待添加节所在的 slide
        // 当 i = slideList.length 时，添加的是末尾的空节
        this.transaction(() => {
            let sectionSlideCount = 0;
            for (let i = 0; i < slideList.length + 1; i += 1) {
                const sectionSlideLength = sectionSlideCount;
                const slideBindSections = sectionList.filter(
                    (section) => section.props.firstSlideId === slideList[i]?.id,
                );
                sectionSlideCount += slideBindSections.length;
                sectionSlideCount += 1;
                // 找到了待添加的节应当绑定的 slide
                if (sectionSlideCount > gapIndex) {
                    // 获取重命名的 nameList
                    const renameList = slideBindSections.map((section) => section.props.name);
                    renameList.splice(gapIndex - sectionSlideLength, 0, sectionName);
                    // 添加节并绑定 slide
                    this.transaction(() => {
                        this.request([
                            new AddSectionMutation({
                                sectionId,
                                name: sectionName,
                            }),
                            new BindSectionToSlideMutation({
                                sectionId,
                                firstSlideIndex: i,
                            }),
                        ]);
                    });
                    // 添加节之后，对节进行重命名
                    const renameSections = this.model.sectionList.filter(
                        (section) => section.props.firstSlideId === slideList[i]?.id,
                    );
                    this.transaction(() => {
                        renameSections.forEach((section, index) => {
                            if (section.props.name === renameList[index]) {
                                return;
                            }
                            section.api.updateProps({ name: renameList[index] });
                        });
                    });
                    return;
                }
            }
        });
    }

    /**
     * 删除一个节。本属于该被删除节的幻灯片会被归至其上一个节中
     * @param sectionIndex 待删除节的索引
     */
    @record()
    public removeSections(sections: ISectionDataModel[]) {
        if (!sections.length) return;

        this.guard((presentation) =>
            Rule.forEach(sections, (section) => Rule.section.should.exist(presentation, section.id)),
        );

        this.transaction(() => {
            this.request(sections.map((section) => new RemoveSectionMutation({ sectionId: section.id })));
        });
    }

    /**
     * 移动一整节到目标位置，页会跟随同时移动
     * @param section 待移动的章节
     * @param toSectionIndex 移动目标章节索引
     */
    @record()
    public moveSection(section: ISectionDataModel, toSectionIndex: number) {
        const { sectionList, slideList } = this.model;
        const sectionFromIndex = sectionList.findIndex((item) => item.id === section.id);
        const firstSlideIndex = this.model.store.getSectionBindingSlideIndex(section as SectionDataModel);
        const moveSlides = slideList.slice(firstSlideIndex, firstSlideIndex + section.props.numSlides);
        const slideToIndex = this.model.store.getSectionBindingSlideIndex(
            sectionList[toSectionIndex] as SectionDataModel,
        );

        this.transaction(() => {
            // 节移出，顶部节需要递沿
            const sectionHelper = new SectionMutationHelper(slideList, sectionList, this.bindSectionToSlide.bind(this));
            sectionHelper.setIgnoreSlides(moveSlides);
            for (const slide of moveSlides) {
                sectionHelper.rebindBySlide(slide.id);
            }
            // 节移入，空节和非空节两种情况：
            const gapIndex =
                sectionList
                    .slice(0, toSectionIndex)
                    .reduce((index, section) => index + section.props.numSlides + 1, 0) - 1;
            if (moveSlides.length > 0) {
                gapIndex >= 0 && sectionHelper.rebindWhenInsertSlide(moveSlides[0].id, gapIndex);
                sectionHelper.addBindMutation(section, moveSlides[0].id);
            } else {
                sectionHelper.rebindBySlide(sectionList[toSectionIndex]?.props.firstSlideId);
                sectionHelper.addBindMutation(section, sectionList[toSectionIndex]?.props.firstSlideId);
            }
            // 计算节的重命名列表
            const renameList = sectionList.map((i) => i.props.name);
            const destIndex = sectionFromIndex >= toSectionIndex ? toSectionIndex : toSectionIndex - 1;
            move(renameList, sectionFromIndex, destIndex);

            // 忽略节，移动 slides
            this.moveSlides(moveSlides as ISlideDataModel[], slideToIndex);

            sectionHelper.requestBindTask();
            sectionHelper.updateSectionList(this.model.sectionList); // 经历了节和slide的移动，需要保证 section 的顺序
            sectionHelper.requestRenameTask(renameList);
        });
    }

    /**
     * 更新图片元信息
     * @param id 图片 ID
     * @param imageDescriptor 图片元信息
     */
    @record()
    public updateImageResourceMap(id: string, imageDescriptor: Omit<IImageResourceData, 'id'>) {
        this.transaction(() => {
            this.request([
                new UpdateResourceMapMutation({
                    resourceType: ResourceType.Image,
                    id,
                    imageDescriptor,
                }),
            ]);
        });
    }

    /**
     * 更新视频元信息
     * @param id 视频 ID
     * @param videoDescriptor 视频元信息
     */
    @record()
    public updateVideoResourceMap(id: string, videoDescriptor: Omit<IVideoResourceData, 'id'>) {
        this.transaction(() => {
            this.request([
                new UpdateResourceMapMutation({
                    resourceType: ResourceType.Video,
                    id,
                    videoDescriptor,
                }),
            ]);
        });
    }

    /**
     * 更新音频元信息
     * @param id 音频 ID
     * @param audioDescriptor 音频元信息
     */
    @record()
    public updateAudioResourceMap(id: string, audioDescriptor: Omit<IAudioResourceData, 'id'>) {
        this.transaction(() => {
            this.request([
                new UpdateResourceMapMutation({
                    resourceType: ResourceType.Audio,
                    id,
                    audioDescriptor,
                }),
            ]);
        });
    }

    /**
     * 更新字体信息
     * @param id 字体 ID
     * @param fontDescriptor 字体元信息
     */
    @record()
    public updateFontResourceMap(id: string, fontDescriptor: Omit<IFontResourceData, 'id'>) {
        this.transaction(() => {
            this.request([
                new UpdateResourceMapMutation({
                    resourceType: ResourceType.Font,
                    id,
                    fontDescriptor,
                }),
            ]);
        });
    }

    @record()
    public updateResourceMap(data: IResourceMapData) {
        this.transaction(() => {
            data.audios &&
                Object.keys(data.audios).forEach(
                    (id) => !this.model.getAudioResourceById(id) && this.updateAudioResourceMap(id, data.audios[id]),
                );
            data.images &&
                Object.keys(data.images).forEach(
                    (id) => !this.model.getImageResourceById(id) && this.updateImageResourceMap(id, data.images[id]),
                );
            data.videos &&
                Object.keys(data.videos).forEach(
                    (id) => !this.model.getVideoResourceById(id) && this.updateVideoResourceMap(id, data.videos[id]),
                );
            data.fonts &&
                Object.keys(data.fonts).forEach(
                    (id) => !this.model.getFontResourceById(id) && this.updateFontResourceMap(id, data.fonts[id]),
                );
        });
    }

    /**
     * 主题字体更新
     *
     * 此接口会更新所有主题的字体
     * @param fontFamily 新字体
     * @returns
     */
    @record()
    public updateThemeFontFamily(fontFamily: string) {
        this.forEachInTransaction(this.model.themeList, (theme) => {
            // 检查是否是 2.0 的数据，这里以 defaultTextStyle 为标志判断：
            // 3.0 数据中 defaultTextStyle 里均使用的是主题字体；2.0 数据中 defaultTextStyle 里使用的是具体的字体类型
            const font = this.model.store.data.defaultTextStyle?.lvl1pPr?.defRPr?.ea?.typeface;
            if (font && !ThemeContext.shouldUseSchemeFont(font)) {
                // 2.0 数据进行数据还原，恢复主题字体的使用
                // 母版使用主题字体
                this.model.masterList.forEach((master) => master.api.useThemeFontInTextStyle());
                // presentation 使用主题字体
                this.useThemeFontInDefaultTextStyle();
            }
            theme.api.updateThemeFontFamily(fontFamily, 'major');
            theme.api.updateThemeFontFamily(fontFamily, 'minor');
        });
    }

    /**
     * 添加主题
     */
    @record()
    public addTheme(themes: IThemeData[]) {
        this.guard(() => Rule.forEach(themes, (theme) => Rule.theme.should.hasCorrectFormat(theme)));

        // 过滤掉 name 和 themeElement 都为空的 theme
        const themesFilter = themes.filter((theme: IThemeData) => theme.name || theme.themeElements);
        const idsMap = new Map<string, string>();
        if (!themesFilter.length) return idsMap;

        themesFilter.length > 0 &&
            this.transaction(() => {
                themesFilter.forEach((theme: IThemeData) => {
                    const themeId = this.model.getIDGenerator().next();
                    const { name, themeElements = {} } = theme;
                    idsMap.set(theme.id, themeId);

                    // 默认值填充
                    if (themeElements.clrScheme === undefined) {
                        themeElements.clrScheme = themeTemplate.themeElements.clrScheme;
                    }
                    if (themeElements.fontScheme === undefined) {
                        themeElements.fontScheme = themeTemplate.themeElements.fontScheme;
                    }
                    if (themeElements.fmtScheme === undefined) {
                        themeElements.fmtScheme = themeTemplate.themeElements.fmtScheme;
                    }

                    this.request([
                        new AddThemeMutation({
                            themeId,
                            themeName: name || '',
                            themeElements,
                        }),
                    ]);
                });
            });
        return idsMap;
    }

    /**
     * 移除 Theme
     * @param themeId 需要移除的 theme id
     */
    @record()
    public removeThemeById(themeId: string) {
        this.guard((presentation) => Rule.theme.should.notUsedInMaster(presentation, themeId));

        this.transaction(() => {
            this.request([
                new RemoveThemeMutation({
                    themeId,
                }),
            ]);
        });
    }

    @record()
    public updateSlideGuideList(slideGuideList: ICTExtendedGuide[], canUndo = false) {
        this.transaction(() => {
            const mut = new SetPresentationPropertiesMutation({
                slideGuideList: {
                    guide: slideGuideList.map((guide, index) => ({ ...guide, id: `${index + 1}` })),
                },
            });
            mut.invertible = canUndo;
            this.request([mut]);
        });
    }

    /**
     * 设置主题
     * name 和 themeElements 可选
     */
    @record()
    public setThemeById(themeId: string, theme: ICTBaseStylesDelta, name?: string) {
        this.model.getThemeById(themeId)?.api.updateTheme(theme, name);
    }

    @record()
    public replacePresentation(presentationData: IPresentationData) {
        this.transaction(() => {
            this.removeSections(this.model.sectionList);
            this.removeSlides(this.model.slideList);
            this.removeLayouts(this.model.layoutList);
            this.removeMasters(this.model.masterList);
            // this.removeNoteSlides([...this.model.noteSlideMap.values()]);
            // this.removeNodeSlideMasters([...this.model.notesMasterMap.values()]);
            // this.model.themeList.forEach((themeDataModel) => this.removeThemeById(themeDataModel.id));
            this.model.themeList
                .map((theme) => theme.id)
                .forEach((id) => {
                    const noteMasterThemeIds = [...this.model.notesMasterMap.values()].map(
                        (model) => model.props.themeId,
                    );
                    if (!noteMasterThemeIds.includes(id)) {
                        this.removeThemeById(id);
                    }
                });

            const getFallbackData = <K extends keyof IPresentationData>(key: K) =>
                presentationData[key] ?? this.model.store.data[key];
            this.request([
                new SetSlideSizeMutation({ size: getFallbackData('sldSz') }),
                new SetNotesSizeMutation({ size: getFallbackData('notesSz') }),
                new SetPresentationPropertiesMutation({
                    defaultTextStyle: getFallbackData('defaultTextStyle'),
                    tableStyleList: getFallbackData('tblStyleList'),
                    showSpecialPlsOnTitleSld: getFallbackData('showSpecialPlsOnTitleSld'),
                    firstSlideNum: getFallbackData('firstSlideNum'),
                    embeddedFontList: getFallbackData('embeddedFontList'),
                }),
            ]);
            this.updateSlideGuideList(presentationData.slideGuideList ?? []);
            this.addPresentation(presentationData, 0);
        });
    }

    @record()
    public clearPresentation() {
        this.transaction(() => {
            this.removeSections(this.model.sectionList);
            this.removeSlides(this.model.slideList);
            this.removeLayouts(this.model.layoutList);
            this.removeMasters(this.model.masterList);
            this.removeNoteSlides([...this.model.noteSlideMap.values()]);
            this.removeNodeSlideMasters([...this.model.notesMasterMap.values()]);
            [...this.model.themeList].forEach((themeDataModel) => this.removeThemeById(themeDataModel.id));
        });
    }

    /**
     * 修改页面尺寸，不缩放元素
     * @param newSlideWidth
     * @param newSlideHeight
     */
    @record()
    public updateCustomSlideSizeWithNoScale(newSlideWidth: number, newSlideHeight: number, type: STSlideSizeType) {
        const { cx: width, cy: height } = this.model.props.sldSz;
        const targetHeight = newSlideHeight;
        const targetWidth = newSlideWidth;
        const scaleX = targetWidth / width;
        const scaleY = targetHeight / height;
        const offsetX = Math.round((targetWidth - width) / 2);
        const offsetY = Math.round((targetHeight - height) / 2);

        this.transaction(() => {
            this.request([
                new SetSlideSizeMutation({
                    size: {
                        cx: targetWidth,
                        cy: targetHeight,
                        type,
                    },
                }),
            ]);
            this.updateMasterAndLayoutWithScale(scaleX, scaleY, false, true);

            for (const slide of this.model.slideList) {
                for (const shape of slide.shapeList) {
                    this.moveShapeFrame(shape, offsetX, offsetY);
                }
            }
        });
    }

    @record()
    public updateCustomSlideSizeWithEnlargeOrReduce(
        newSlideWidth: number,
        newSlideHeight: number,
        isEnlarge: boolean,
        type: STSlideSizeType,
    ) {
        const { cx: width, cy: height } = this.model.props.sldSz;
        if (width === newSlideWidth && height === newSlideHeight) {
            return;
        }

        const scaleX: number = newSlideWidth / width;
        const scaleY: number = newSlideHeight / height;
        const isScaleX = isEnlarge ? scaleX >= scaleY : scaleX < scaleY;

        this.transaction(() => {
            this.request([
                new SetSlideSizeMutation({
                    size: {
                        cx: newSlideWidth,
                        cy: newSlideHeight,
                        type,
                    },
                }),
            ]);

            // 任何情况，master和layout都做等比缩放
            this.updateMasterAndLayoutWithScale(scaleX, scaleY, true, isScaleX);

            const widthDiff = newSlideWidth - width;
            const heightDiff = newSlideHeight - height;
            for (const slide of this.model.slideList) {
                // 更新最外层形状的位置和宽高
                for (const shape of slide.shapeList) {
                    this.moveShapeFrameWithScale(shape, isScaleX, widthDiff, heightDiff, scaleX, scaleY, width, height);
                    this.scaleShape(shape, isScaleX ? scaleX : scaleY);
                }
            }
        });
    }

    /**
     * 修改页面尺寸，缩放元素
     * @param newSlideWidth
     * @param newSlideHeight
     * @param scale
     */
    @record()
    public updateCustomSlideSizeWithScale(
        newSlideWidth: number,
        newSlideHeight: number,
        scale: number,
        type: STSlideSizeType,
    ) {
        const { cx: width, cy: height } = this.model.props.sldSz;
        if (width === newSlideWidth && height === newSlideHeight) {
            return;
        }

        const isScaleX = this.getMainDirectionIsX(newSlideWidth, newSlideHeight, scale);
        const scaleX: number = newSlideWidth / width;
        const scaleY: number = newSlideHeight / height;

        this.transaction(() => {
            this.request([
                new SetSlideSizeMutation({
                    size: {
                        cx: newSlideWidth,
                        cy: newSlideHeight,
                        type,
                    },
                }),
            ]);

            // 任何情况，master和layout都做等比缩放
            this.updateMasterAndLayoutWithScale(scaleX, scaleY, true, isScaleX);

            const widthDiff = newSlideWidth - width;
            const heightDiff = newSlideHeight - height;
            for (const slide of this.model.slideList) {
                // 更新最外层形状的位置和宽高
                for (const shape of slide.shapeList) {
                    this.moveShapeFrameWithScale(shape, isScaleX, widthDiff, heightDiff, scaleX, scaleY, width, height);
                    this.scaleShape(shape, isScaleX ? scaleX : scaleY);
                }
            }
        });
    }

    /**
     * 整体缩放 presentation，不同于比例变更
     * @param targetWidth
     * @param targetHeight
     * @param slideSizeType
     * @param scaleX
     * @param scaleY
     * @param offsetX
     * @param offsetY
     * @param scaleRadio
     */
    private scalePresentation(
        targetWidth: number,
        targetHeight: number,
        slideSizeType: STSlideSizeType,
        scaleX: number,
        scaleY: number,
        scaleRadio: number,
    ) {
        this.transaction(() => {
            this.request([
                new SetSlideSizeMutation({
                    size: {
                        cx: targetWidth,
                        cy: targetHeight,
                        type: slideSizeType,
                    },
                }),
            ]);

            for (const master of this.model.masterList) {
                for (const shape of master.shapeList) {
                    this.scaleShapeFrame(shape, scaleX, scaleY);
                    this.scaleShape(shape, scaleRadio);
                }
            }

            for (const layout of this.model.layoutList) {
                for (const shape of layout.shapeList) {
                    this.scaleShapeFrame(shape, scaleX, scaleY);
                    this.scaleShape(shape, scaleRadio);
                }
            }

            for (const slide of this.model.slideList) {
                for (const shape of slide.shapeList) {
                    this.scaleShapeFrame(shape, scaleX, scaleY);
                    this.scaleShape(shape, scaleRadio);
                }
            }
        });
    }

    private scaleShape(shape: IDrawingShapeDataModel, scaleRadio: number) {
        const pageId = shape.getPage().id;
        const shapeId = shape.id;
        // 组合的情况
        if (shape.type === DataType.GroupShape) {
            this.adaptInnerGroupShape(pageId, shape, scaleRadio);
        }

        // 图形、文本的情况
        if (shape.type === DataType.Shape && shape.getOwnedData().txBody?.p) {
            const { txBody } = shape.getOwnedData();
            txBody && this.scaleTextBody(pageId, shapeId, txBody, scaleRadio);
        }

        // 表格的情况
        if (shape.type === DataType.GraphicalObjectFrameTable) {
            const tableData = shape.getCloneData();
            this.scaleTable(pageId, shapeId, tableData, scaleRadio, shape);
        }

        // SmartArt 的情况：较为特殊，直接调用smartArt暴露的函数，直接处理子元素大小及文字大小
        if (shape.type === DataType.GraphicalObjectFrameSmartArt) {
            const smartArt = shape as SmartArtDataModel;
            smartArt.api.computeChildTransform({ scaleX: scaleRadio, scaleY: scaleRadio });
        }
    }

    private scaleTable(pageId: string, shapeId: string, table: ITableData, scaleRadio: number, shape: ITableDataModel) {
        if (table?.graphic?.graphicData?.tbl?.tblGrid?.gridCol) {
            const widths: IWidthInfo[] = [];

            // 对表格宽高数据进行处理
            let index = 0;
            for (const tblCol of table?.graphic?.graphicData?.tbl?.tblGrid?.gridCol) {
                tblCol.w && (tblCol.w = tblCol.w * scaleRadio);
                widths.push({
                    index,
                    width: tblCol.w,
                });
                index += 1;
            }

            shape.api.setTableColWidths(widths);
        }
        if (table?.graphic?.graphicData?.tbl?.tr) {
            const heights: IHeightInfo[] = [];
            for (let rowIndex = 0; rowIndex < table?.graphic?.graphicData?.tbl?.tr.length; rowIndex++) {
                const tblRow = table?.graphic?.graphicData?.tbl?.tr[rowIndex];
                tblRow.h && (tblRow.h = tblRow.h * scaleRadio);
                tblRow.tc?.forEach(
                    (tc, tcIndex) =>
                        tc.txBody &&
                        this.scaleTextBody(pageId, shapeId, tc.txBody, scaleRadio, { row: rowIndex, col: tcIndex }),
                );

                heights.push({
                    index: rowIndex,
                    height: tblRow.h,
                });
            }

            shape.api.setTableRowHeights(heights);
        }
    }

    private scaleTextBody(
        pageId: string,
        shapeId: string,
        textBody: DeepReadonly<ICTTextBody>,
        scaleRadio: number,
        tableOptions?: { col: number; row: number },
    ) {
        if (textBody.p?.length) {
            const cloneP: ICTTextParagraph[] = cloneDeep(textBody.p);
            cloneP.forEach((p) => {
                p.pPr && this.scaleParagraphProperties(p.pPr, scaleRadio);
                p.endParaRPr?.sz && (p.endParaRPr.sz = p.endParaRPr.sz * scaleRadio);
                p.egTextRun?.forEach((run) => {
                    run.r?.rPr?.sz && (run.r.rPr.sz = run.r.rPr.sz * scaleRadio);
                    run.br?.rPr?.sz && (run.br.rPr.sz = run.br.rPr.sz * scaleRadio);
                    run.fld?.rPr?.sz && (run.fld.rPr.sz = run.fld.rPr.sz * scaleRadio);
                    run.fld?.pPr && this.scaleParagraphProperties(run.fld.pPr, scaleRadio);
                });
            });
            this.transaction(() => {
                if (tableOptions) {
                    this.request([
                        new SetTextDataMutation(
                            TextDataUtils.fromParagraphListData(
                                cloneP,
                                pageId,
                                shapeId,
                                tableOptions.col,
                                tableOptions.row,
                            ),
                        ),
                    ]);
                } else {
                    this.request([
                        new SetTextDataMutation(
                            shallowOmit(TextDataUtils.fromParagraphListData(cloneP, pageId, shapeId), 'type'),
                        ),
                    ]);
                }
            });
        }
        if (textBody.lstStyle) {
            const cloneLstStyle: ICTTextListStyle = cloneDeep(textBody.lstStyle);
            Object.keys(cloneLstStyle).forEach((key) => {
                if (key === 'extLst') return;
                const item: ICTTextParagraphProperties = (cloneLstStyle as any)[key];
                this.scaleParagraphProperties(item, scaleRadio);
            });
            this.transaction(() => {
                if (tableOptions) {
                    this.request([
                        new SetTextBodyPropertiesMutation(
                            shallowOmit(
                                TextBodyPropertiesUtils.fromTextBodyProperties(
                                    {
                                        lstStyle: null,
                                    },
                                    pageId,
                                    shapeId,
                                    tableOptions.col,
                                    tableOptions.row,
                                ),
                                'type',
                            ),
                        ),
                        new SetTextBodyPropertiesMutation(
                            shallowOmit(
                                TextBodyPropertiesUtils.fromTextBodyProperties(
                                    {
                                        lstStyle: cloneLstStyle,
                                    },
                                    pageId,
                                    shapeId,
                                    tableOptions.col,
                                    tableOptions.row,
                                ),
                                'type',
                            ),
                        ),
                    ]);
                } else {
                    this.request([
                        new SetTextBodyPropertiesMutation(
                            shallowOmit(
                                TextBodyPropertiesUtils.fromTextBodyProperties(
                                    {
                                        lstStyle: null,
                                    },
                                    pageId,
                                    shapeId,
                                ),
                                'type',
                            ),
                        ),
                        new SetTextBodyPropertiesMutation(
                            shallowOmit(
                                TextBodyPropertiesUtils.fromTextBodyProperties(
                                    {
                                        lstStyle: cloneLstStyle,
                                    },
                                    pageId,
                                    shapeId,
                                ),
                                'type',
                            ),
                        ),
                    ]);
                }
            });
        }

        // 自缩排的文本scale无法通过数据计算，让上层在渲染后重新算一次，所以这块需要删除一下
        if (textBody.bodyPr?.normAutofit) {
            const cloneBodyPr: ICTTextBodyProperties = cloneDeep(textBody.bodyPr);
            cloneBodyPr.normAutofit && (cloneBodyPr.normAutofit.fontScale = undefined);
            this.request([
                new SetTextBodyPropertiesMutation(
                    shallowOmit(
                        TextBodyPropertiesUtils.fromTextBodyProperties(
                            {
                                bodyPr: null,
                            },
                            pageId,
                            shapeId,
                        ),
                        'type',
                    ),
                ),
                new SetTextBodyPropertiesMutation(
                    shallowOmit(
                        TextBodyPropertiesUtils.fromTextBodyProperties(
                            {
                                bodyPr: cloneBodyPr,
                            },
                            pageId,
                            shapeId,
                        ),
                        'type',
                    ),
                ),
            ]);
        }

        if (textBody.bodyPr?.spAutoFit) {
            const cloneBodyPr: ICTTextBodyProperties = cloneDeep(textBody.bodyPr);
            cloneBodyPr.bIns && (cloneBodyPr.bIns = cloneBodyPr.bIns * scaleRadio);
            cloneBodyPr.lIns && (cloneBodyPr.lIns = cloneBodyPr.lIns * scaleRadio);
            cloneBodyPr.rIns && (cloneBodyPr.rIns = cloneBodyPr.rIns * scaleRadio);
            cloneBodyPr.tIns && (cloneBodyPr.tIns = cloneBodyPr.tIns * scaleRadio);
            this.transaction(() => {
                this.request([
                    new SetTextBodyPropertiesMutation(
                        shallowOmit(
                            TextBodyPropertiesUtils.fromTextBodyProperties(
                                {
                                    bodyPr: null,
                                },
                                pageId,
                                shapeId,
                            ),
                            'type',
                        ),
                    ),
                    new SetTextBodyPropertiesMutation(
                        shallowOmit(
                            TextBodyPropertiesUtils.fromTextBodyProperties(
                                {
                                    bodyPr: cloneBodyPr,
                                },
                                pageId,
                                shapeId,
                            ),
                            'type',
                        ),
                    ),
                ]);
            });
        }
    }

    /**
     * 段落缩进相关
     * @param pPr
     * @param scaleRadio
     */
    private scaleParagraphProperties(pPr: ICTTextParagraphProperties, scaleRadio: number) {
        pPr?.indent && (pPr.indent = pPr.indent * scaleRadio);
        pPr?.marL && (pPr.marL = pPr.marL * scaleRadio);

        // 段落行间距不缩放
        // pPr?.lnSpc?.spcPct?.val && (pPr.lnSpc.spcPct.val = pPr.lnSpc.spcPct.val * scaleRadio);
        pPr?.spcAft?.spcPct?.val && (pPr.spcAft.spcPct.val = pPr.spcAft.spcPct.val * scaleRadio);
        pPr?.spcBef?.spcPct?.val && (pPr.spcBef.spcPct.val = pPr.spcBef.spcPct.val * scaleRadio);
    }

    /**
     * 处理组合元素的缩放，递归缩放组合内所有元素文字的大小
     */
    private adaptInnerGroupShape(pageId: string, groupShape: IGroupShapeDataModel, scaleRadio: number) {
        if (groupShape.shapeList?.length) {
            groupShape.shapeList.forEach((element) => {
                if (element.type === DataType.GroupShape) {
                    // 递归处理组合图形
                    this.adaptInnerGroupShape(pageId, element, scaleRadio);
                }
                if (element.type === DataType.Shape && element.getOwnedData().txBody?.p) {
                    const textBody = element.getOwnedData().txBody;
                    textBody && this.scaleTextBody(pageId, element.id, textBody, scaleRadio);
                }
            });
        }
    }

    private scaleShapeFrame(shape: IDrawingShapeDataModel, scaleX: number, scaleY: number) {
        if (!shape.getOwnedData().spPr.xfrm) return;

        const { off, ext } = shape.props.spPr.xfrm ?? {};
        const { left, top, width, height } = shape.getFrame().scale(scaleX, scaleY).floor();

        shape.api.updateTransform({
            off: off && {
                x: left,
                y: top,
            },
            ext: ext && {
                cx: width,
                cy: height,
            },
        });
    }

    private moveShapeFrame(shape: IDrawingShapeDataModel, offsetX: number, offsetY: number) {
        if (!shape.getOwnedData().spPr.xfrm) return;

        const { left, top } = shape.getFrame().move(offsetX, offsetY).floor();

        shape.api.updateTransform({
            off: {
                x: left,
                y: top,
            },
        });
    }

    private duplicateSlides(slideDataModels: ISlideDataModel[]) {
        const slides = this.model.store.slideList.intersectSorted(slideDataModels);
        const toIndex = this.model.store.slideList.findIndexById(slides[slideDataModels.length - 1].id) + 1;
        const slideDatas = slides.map((slideDataModel) => slideDataModel.getCloneData());
        this.addSlides(slideDatas, toIndex);
    }

    /**
     * 复制版式，复制的版式会添加到最后一个待复制版式的后面
     * @param layoutDataModels 待复制版式
     */
    private duplicateLayouts(layoutDataModels: ILayoutDataModel[]) {
        const layoutsByMaster = this.getLayoutsMasterMap(layoutDataModels);
        if (layoutsByMaster.size === 0) return;

        this.transaction(() => {
            const masters = this.model.store.masterList.intersectSorted([...layoutsByMaster.keys()]);
            const lastMaster = masters[masters.length - 1];
            if (!lastMaster) return;

            const layouts = lastMaster.store.layoutList.intersectSorted(layoutsByMaster.get(lastMaster) ?? []);
            // 获取最后一个版式相对其 master 的位置
            let toIndex = lastMaster.store.layoutList.findIndexById(layouts[layouts.length - 1].id) + 1;

            for (const master of masters) {
                const layoutList = layoutsByMaster.get(master) ?? [];
                if (!layoutList.length) continue;

                const layouts = master.store.layoutList.intersectSorted(layoutList);
                const layoutDataList = layouts.map((layout) => layout.getCloneData());
                for (const layoutData of layoutDataList) {
                    // 需要重写 masterId 为最后一个母版
                    layoutData.masterId = lastMaster.id;
                }
                if (lastMaster) {
                    this.addLayouts(layoutDataList, toIndex);
                    // 添加后，目标位置需要递增
                    toIndex += layoutDataList.length;
                }
            }
        });
    }

    /**
     * 用于在 addPage api 中生成 keyframe context
     * @param idsMap 用于生成新 id 的新旧 id 映射
     */
    private createKeyframeContext(idsMap: Map<string, string>) {
        return {
            presentation: this.model.store.data,
            idFactory: (oldId: string) => idsMap.get(oldId) ?? oldId,
        };
    }

    /**
     * 已知间隙索引，求此间隙对应的节索引
     * @param gapIndex UI层间隙索引
     */
    private getSectionIndexFromGap(gapIndex: number) {
        const { sectionList } = this.model;
        let count = 0;
        let sectionIndex = 0;
        for (sectionIndex = 0; sectionIndex < sectionList.length; sectionIndex += 1) {
            count += 1;
            count += sectionList[sectionIndex].props.numSlides;
            if (gapIndex < count) {
                return sectionIndex;
            }
        }
        return sectionIndex;
    }

    /**
     * 在添加节的时候，转换间隙便于计算：
     * UI层间隙索引的规则：有节的时候第一个节下方为第一个间隙，没有节的时候第一页上方为第一个间隙
     * 转换为：平铺的节和页的列表里，第一个 model 上方为第一个间隙
     * @param originIndex UI层索引
     */
    private getGapIndex(originIndex: number | undefined) {
        const { slideList, sectionList } = this.model;
        if (originIndex === undefined) {
            return slideList.length + sectionList.length;
        }
        return sectionList.length > 0 ? originIndex + 1 : originIndex;
    }

    private bindSectionToSlide(section: ISectionDataModel, firstSlideIndex: number) {
        this.transaction(() => {
            this.request([
                new BindSectionToSlideMutation({
                    sectionId: section.id,
                    firstSlideIndex,
                }),
            ]);
        });
    }

    private createIdsMapFromPage(page: IPageData) {
        const idsMap = new Map<string, string>();
        const idGenerator = this.model.getIDGenerator();
        const { spTree } = page.cSld;
        // 如果有 shapeList，需要处理对应的 ids
        // TODO @siran 解决 tests/operations-replay.spec.ts
        if (spTree) {
            const pageIdsMap = createIdsMapFromShapes(spTree.shapeList, idGenerator);
            [...pageIdsMap.entries()].forEach(([key, value]) => idsMap.set(key, value));
        }
        idsMap.set(page.id, idGenerator.next());
        if (spTree) {
            spTree.id = spTree.id ?? `spTree_${page.id}`;
            idsMap.set(spTree.id, idGenerator.next());
        }
        return idsMap;
    }

    /**
     * 让 presentation 使用主题字体：
     * 由于 2.0 数据覆盖了主题字体，若 presentation 需要使用主题字体，需要对数据进行修改
     */
    private useThemeFontInDefaultTextStyle() {
        const lstStyleDelta: ICTTextListStyleDelta = Array.from({ length: 9 }, (i, index) => index + 1).reduce(
            (textStyles, curIndex) => {
                return {
                    ...textStyles,
                    [`lvl${curIndex}pPr`]: {
                        defRPr: { latin: { typeface: '+mn-lt' }, ea: { typeface: '+mn-ea' } },
                    },
                };
            },
            {},
        );
        this.updateProps({
            defaultTextStyle: lstStyleDelta,
        });
    }

    /**
     * x、y轴同时放大或缩小时，判断主轴
     * @param newWidth
     * @param newHeight
     * @param scale
     */
    private getMainDirectionIsX(newWidth: number, newHeight: number, scale: number): boolean {
        const isEnLarge = scale >= 1;

        const { cx: width, cy: height } = this.model.props.sldSz;

        const scaleX = newWidth / width - 1;
        const scaleY = newHeight / height - 1;

        if (scaleX === 0) return false;
        if (scaleY === 0) return true;

        // 画布放大，只有一个方向变更，变更的做主轴；两个都变更，变更比例更小的轴做为主轴
        if (isEnLarge) {
            return scaleX <= scaleY;
        }

        // 画布缩小，那缩小比例更大的轴做为主轴，实际用起来，office这段逻辑是非对称的，后续可以看看要不要改成对称的
        // return scaleX >= scaleY; // 对称
        return scaleX <= scaleY; // 不对称
    }

    /**
     * 处理交叉轴偏移
     * @param slideLength
     * @param elementPosition
     * @param scale
     */
    private handleCrossOffset(slideLength: number, elementPosition: number, scale: number) {
        const centerLength = slideLength / 2;
        return (elementPosition - centerLength) * (scale - 1);
    }

    /**
     * 基于缩放后的画布尺寸与比例处理元素
     * @param shape
     * @param isScaleX
     * @param widthDiff
     * @param heightDiff
     * @param scaleX
     * @param scaleY
     * @param originWidth
     * @param originHeight
     * @private
     */
    private moveShapeFrameWithScale(
        shape: IDrawingShapeDataModel,
        isScaleX: boolean,
        widthDiff: number,
        heightDiff: number,
        scaleX: number,
        scaleY: number,
        originWidth: number,
        originHeight: number,
    ) {
        if (!shape.getOwnedData().spPr.xfrm) return;
        const { left: originLeft, top: originTop } = shape.getFrame().floor();

        let offsetX = 0;
        let offsetY = 0;
        const scale = isScaleX ? scaleX : scaleY;

        if (isScaleX) {
            offsetX = originLeft * (scale - 1);
            offsetY = this.handleCrossOffset(originHeight, originTop, scale);
            offsetY += heightDiff / 2;
        } else {
            offsetY = originTop * (scale - 1);
            offsetX = this.handleCrossOffset(originWidth, originLeft, scale);
            offsetX += widthDiff / 2;
        }

        const { left, top } = shape.getFrame().move(offsetX, offsetY).floor();
        const { width, height } = shape.getFrame().scale(scale, scale).floor();

        shape.api.updateTransform({
            off: {
                x: left,
                y: top,
            },
            ext: {
                cx: width,
                cy: height,
            },
        });
    }

    private getLayoutsMasterMap(layoutDataModels: ILayoutDataModel[]): Map<MasterDataModel, ILayoutDataModel[]> {
        const layoutsMasterMap = new Map<MasterDataModel, ILayoutDataModel[]>();
        layoutDataModels.forEach((layout) => {
            const master = layout.getMaster() as MasterDataModel;
            const layouts = layoutsMasterMap.get(master) ?? [];
            layouts.push(layout);
            layoutsMasterMap.set(master, layouts);
        });
        return layoutsMasterMap;
    }

    /**
     * 目前只支持16:9和4:3
     * @param sz
     * @returns
     */
    private getValidSldSz(sz: STSlideSizeType) {
        if ([STSlideSizeType.screen16x9, STSlideSizeType.screen4x3]) return sz;
        return STSlideSizeType.screen16x9;
    }

    /**
     * 缩放master的默认继承信息
     * @param scaleX
     * @param scaleY
     * @param needScaleDefaultStyles
     * @param scale
     */
    private scaleMaster(scaleX: number, scaleY: number, needScaleDefaultStyles: boolean, scale: number) {
        for (const master of this.model.masterList) {
            for (const shape of master.shapeList) {
                this.scaleShapeFrame(shape, scaleX, scaleY);
            }

            if (needScaleDefaultStyles) {
                this.scaleMasterTitleStyle(master, scale);
                this.scaleMasterBodyStyle(master, scale);
                this.scaleMasterOtherStyle(master, scale);
            }
        }
    }

    /**
     * 缩放master的title样式，用于占位符等继承属性的缩放
     * @param master
     * @param scale
     */
    private scaleMasterTitleStyle(master: IMasterDataModel, scale: number) {
        const masterData = master.getOwnedData();
        const styleScale = this.getStyleScale(scale);
        if (masterData.txStyles?.titleStyle?.lvl1pPr) {
            // title只有lvl1
            const { defTabSz, defRPr } = masterData.txStyles.titleStyle.lvl1pPr;
            const sz = defRPr?.sz;
            const txStyles: ICTSlideMasterTextStylesDelta = {};
            if (defTabSz) {
                set(txStyles, 'titleStyle.lvl1pPr.defTabSz', defTabSz * styleScale);
            }

            if (sz) {
                set(txStyles, 'titleStyle.lvl1pPr.defRPr.sz', sz * styleScale);
            }

            master.api.updateProps<IMasterDataDelta>({
                txStyles,
            });
        }
    }

    /**
     * 缩放master的body样式，用于占位符等继承属性的缩放
     * @param master
     * @param scale
     */
    private scaleMasterBodyStyle(master: IMasterDataModel, scale: number) {
        const masterData = master.getOwnedData();
        const styleScale = this.getStyleScale(scale);
        const txStyles: ICTSlideMasterTextStylesDelta = cloneDeep(masterData.txStyles);
        if (masterData.txStyles?.bodyStyle) {
            // body是lvl1-lvl9
            for (let index = 1; index <= 9; index++) {
                const key = `lvl${index}pPr` as keyof Omit<ICTTextListStyle, 'defPPr' | 'extLst'>;
                const pPr = masterData.txStyles?.bodyStyle[key];
                if (pPr) {
                    const { defTabSz, defRPr, marL, indent, spcBef } = pPr;
                    const sz = defRPr?.sz;
                    if (defTabSz) {
                        set(txStyles, `bodyStyle.${key}.defTabSz`, defTabSz * styleScale);
                    }

                    if (marL) {
                        set(txStyles, `bodyStyle.${key}.marL`, marL * styleScale);
                    }

                    if (indent) {
                        set(txStyles, `bodyStyle.${key}.indent`, indent * styleScale);
                    }

                    if (sz) {
                        set(txStyles, `bodyStyle.${key}.defRPr.sz`, sz * styleScale);
                    }

                    if (spcBef?.spcPts?.val) {
                        set(txStyles, `bodyStyle.${key}.spcBef.spcPts.val`, spcBef.spcPts.val * styleScale);
                    }
                }
            }

            master.api.updateProps<IMasterDataDelta>({
                txStyles,
            });
        }
    }

    /**
     * 缩放master的body样式，用于占位符等继承属性的缩放
     * todo: 应该可以和bodyStyle合成一个方法
     * @param master
     * @param scale
     */
    private scaleMasterOtherStyle(master: IMasterDataModel, scale: number) {
        const masterData = master.getOwnedData();
        const styleScale = this.getStyleScale(scale);
        const txStyles: ICTSlideMasterTextStylesDelta = cloneDeep(masterData.txStyles);
        if (masterData.txStyles?.otherStyle) {
            // other是lvl1-lvl9
            for (let index = 1; index <= 9; index++) {
                const key = `lvl${index}pPr` as keyof Omit<ICTTextListStyle, 'defPPr' | 'extLst'>;
                const pPr = masterData.txStyles?.otherStyle[key];
                if (pPr) {
                    const { defTabSz, defRPr, marL, indent, spcBef } = pPr;
                    const sz = defRPr?.sz;
                    if (defTabSz) {
                        set(txStyles, `otherStyle.${key}.defTabSz`, defTabSz * styleScale);
                    }

                    if (marL) {
                        set(txStyles, `otherStyle.${key}.marL`, marL * styleScale);
                    }

                    if (indent) {
                        set(txStyles, `otherStyle.${key}.indent`, indent * styleScale);
                    }

                    if (sz) {
                        set(txStyles, `otherStyle.${key}.defRPr.sz`, sz * styleScale);
                    }

                    if (spcBef?.spcPts?.val) {
                        set(txStyles, `otherStyle.${key}.spcBef.spcPts.val`, spcBef.spcPts.val * styleScale);
                    }
                }
            }

            master.api.updateProps<IMasterDataDelta>({
                txStyles,
            });
        }
    }

    /**
     * 缩放layout的默认继承信息
     * @param scaleX
     * @param scaleY
     * @param scale
     */
    private scaleLayout(scaleX: number, scaleY: number, scale: number) {
        const styleScale = this.getStyleScale(scale);
        for (const layout of this.model.layoutList) {
            for (const shape of layout.shapeList) {
                this.scaleShapeFrame(shape, scaleX, scaleY);

                if (shape?.type === DataType.Shape) {
                    const txBody = cloneDeep(shape.getOwnedData()?.txBody);
                    if (txBody?.lstStyle) {
                        const lstStyle: ICTTextListStyle = cloneDeep(txBody.lstStyle) as ICTTextListStyle;

                        // 缩进最多为9级
                        for (let index = 1; index <= 9; index++) {
                            const key = `lvl${index}pPr` as keyof Omit<ICTTextListStyle, 'defPPr' | 'extLst'>;
                            const pPr = lstStyle[key];
                            if (pPr) {
                                const { marL, indent, defRPr } = pPr;
                                const sz = defRPr?.sz;

                                if (marL) {
                                    set(lstStyle, `${key}.marL`, marL * styleScale);
                                }

                                if (indent) {
                                    set(lstStyle, `${key}.indent`, indent * styleScale);
                                }

                                if (sz) {
                                    set(lstStyle, `${key}.defRPr.sz`, sz * styleScale);
                                }
                            }
                        }

                        shape.api.updateTextStyles(lstStyle);
                    }
                }
            }
        }
    }

    /**
     * tlTitle和subTitle获取特殊的scale相对比例
     * @param scale
     */
    private getStyleScale(scale: number): number {
        return scale;

        // todo: ctlTitle和subTitle有一套比较麻烦的scale规则，先忽略
        // if (scale === 1) {
        //     return scale;
        // }
        // if (scale < 1) {
        //     return scale * (1 + 1 / 3);
        // }
        // return scale * (1 - 1 / 3);
    }

    /**
     * 缩放presentation上默认继承信息，例如新建元素的文本大小
     * @param scale
     */
    private scalePresentationDefaultTextStyle(scale: number) {
        const defaultTextStyle: ICTTextListStyle = cloneDeep(this.model.getOwnedData().defaultTextStyle);
        if (!defaultTextStyle) return;

        for (let index = 1; index <= 9; index++) {
            const key = `lvl${index}pPr` as keyof Omit<ICTTextListStyle, 'defPPr' | 'extLst'>;
            const pPr = defaultTextStyle?.[key];
            if (pPr) {
                const { defTabSz, defRPr, marL, indent, spcBef } = pPr;
                const sz = defRPr?.sz;
                if (defTabSz) {
                    set(defaultTextStyle, `${key}.defTabSz`, defTabSz * scale);
                }

                if (marL) {
                    set(defaultTextStyle, `${key}.marL`, marL * scale);
                }

                if (indent) {
                    set(defaultTextStyle, `${key}.indent`, indent * scale);
                }

                if (sz) {
                    set(defaultTextStyle, `${key}.defRPr.sz`, sz * scale);
                }

                if (spcBef?.spcPts?.val) {
                    set(defaultTextStyle, `${key}.spcBef.spcPts.val`, spcBef.spcPts.val * scale);
                }
            }
        }

        this.updateProps({
            defaultTextStyle,
        });
    }

    /**
     * 根据缩放比例更新master和layout
     * master和layout永远等比缩放和移动
     * @param scaleX
     * @param scaleY
     * @param needScaleTxStyles
     * @param isScaleX
     */
    private updateMasterAndLayoutWithScale(
        scaleX: number,
        scaleY: number,
        needScaleTxStyles: boolean,
        isScaleX: boolean,
    ) {
        const scale = isScaleX ? scaleX : scaleY;

        this.scalePresentationDefaultTextStyle(scale);
        this.scaleMaster(scaleX, scaleY, needScaleTxStyles, scale);
        this.scaleLayout(scaleX, scaleY, scale);
    }

    /**
     * 浅拷贝一份 presentation
     */
    private shallowClonePresentation(presentation: IPresentationData) {
        const newLayoutMap: Record<string, ILayoutData> = {};
        for (const [id, layout] of Object.entries(presentation.layoutMap)) {
            newLayoutMap[id] = { ...layout };
        }
        return {
            ...presentation,
            themeList: [...presentation.themeList],
            masterList: presentation.masterList.map((master) => ({ ...master })),
            layoutMap: newLayoutMap,
            slideList: presentation.slideList.map((slide) => ({ ...slide })),
        };
    }
}
